import { useEffect, useState } from "react";
import { ExternalLink, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, fmt, formatApiError } from "../../lib/api";

function ChannelToggle({ product, channel, label, onDone }) {
  const listed = product.channels?.[channel]?.listed;
  const [busy, setBusy] = useState(false);
  const toggle = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/products/${product.id}/channels`, { channel, listed: !listed });
      toast.success(`${product.name} ${!listed ? "listed on" : "removed from"} ${label}${channel !== "shop" ? " (MOCKED sync)" : ""}`);
      onDone();
    } catch (e) {
      toast.error(formatApiError(e));
    }
    setBusy(false);
  };
  return (
    <button
      data-testid={`channel-toggle-${channel}-${product.id}`}
      onClick={toggle}
      disabled={busy}
      className={`rounded-full border px-3 py-1 text-[11px] font-semibold transition-colors disabled:opacity-50 ${
        listed ? "border-emerald-400/40 bg-emerald-400/10 text-emerald-300" : "border-white/10 text-slate-500 hover:text-white"
      }`}
    >
      {busy ? <Loader2 size={11} className="inline animate-spin" /> : null} {label} {listed ? "●" : "○"}
    </button>
  );
}

export default function ProductsAdminPage() {
  const [products, setProducts] = useState([]);
  const [priceEdits, setPriceEdits] = useState({});

  const load = () => api.get("/admin/products").then((r) => setProducts(r.data.products));
  useEffect(() => { load(); }, []);

  const savePrice = async (id) => {
    const price = Number(priceEdits[id]);
    if (!price || price <= 0) return toast.error("Enter a valid price");
    try {
      await api.patch(`/admin/products/${id}`, { price });
      toast.success("Price updated");
      setPriceEdits((p) => ({ ...p, [id]: undefined }));
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div data-testid="admin-products-page">
      <div className="mb-6">
        <p className="eyebrow">Catalog</p>
        <h1 className="font-heading text-2xl font-bold text-white">Products & channels</h1>
      </div>
      <div className="card-surface overflow-x-auto">
        <table className="w-full min-w-[900px] text-sm">
          <thead>
            <tr className="border-b border-white/[0.08] text-left text-xs uppercase tracking-wider text-slate-500">
              <th className="px-5 py-3">Product</th>
              <th className="px-5 py-3">Cost → Price</th>
              <th className="px-5 py-3">Margin</th>
              <th className="px-5 py-3">Trend</th>
              <th className="px-5 py-3">Supplier</th>
              <th className="px-5 py-3">Channels</th>
              <th className="px-5 py-3">Edit price</th>
            </tr>
          </thead>
          <tbody>
            {products.map((p) => (
              <tr key={p.id} className="border-b border-white/[0.04] hover:bg-white/[0.02]" data-testid={`admin-product-row-${p.id}`}>
                <td className="px-5 py-3">
                  <div className="flex items-center gap-3">
                    <img src={p.images[0]} alt="" className="h-10 w-10 rounded-lg object-cover" />
                    <div>
                      <p className="font-medium text-white">{p.name}</p>
                      <p className="text-[11px] text-slate-500">{p.category}{!p.returnable && <span className="text-rose-400"> · non-returnable</span>}</p>
                    </div>
                  </div>
                </td>
                <td className="px-5 py-3 font-mono text-xs text-slate-300">{fmt(p.cost)} → <span className="text-amber-300">{fmt(p.price)}</span></td>
                <td className="px-5 py-3 text-emerald-400">{p.margin}%</td>
                <td className="px-5 py-3 font-mono text-xs text-slate-300">{p.trend_score}</td>
                <td className="px-5 py-3">
                  {p.supplier ? (
                    <a href={p.supplier.url} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-xs text-slate-400 hover:text-amber-300" data-testid={`supplier-link-${p.id}`}>
                      {p.supplier.name.split(" ").slice(0, 2).join(" ")} <ExternalLink size={11} />
                    </a>
                  ) : <span className="text-xs text-slate-600">—</span>}
                </td>
                <td className="px-5 py-3">
                  <div className="flex flex-wrap gap-1.5">
                    <ChannelToggle product={p} channel="shop" label="Shop" onDone={load} />
                    <ChannelToggle product={p} channel="ebay" label="eBay" onDone={load} />
                    <ChannelToggle product={p} channel="tiktok" label="TikTok" onDone={load} />
                  </div>
                </td>
                <td className="px-5 py-3">
                  <div className="flex items-center gap-1.5">
                    <input
                      data-testid={`price-input-${p.id}`}
                      type="number"
                      step="0.01"
                      placeholder={p.price}
                      value={priceEdits[p.id] ?? ""}
                      onChange={(e) => setPriceEdits((s) => ({ ...s, [p.id]: e.target.value }))}
                      className="input-dark !w-24 !px-2 !py-1.5 text-xs"
                    />
                    {priceEdits[p.id] !== undefined && priceEdits[p.id] !== "" && (
                      <button data-testid={`price-save-${p.id}`} onClick={() => savePrice(p.id)} className="rounded-lg bg-amber-400 px-2.5 py-1.5 text-xs font-bold text-black">
                        Save
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
